import React, { useState, useEffect, useRef } from 'react';

const PHASES = [
  '2 sets of 3',
  '1 set of 3 + 1 run of 4',
  '1 set of 4 + 1 run of 4',
  '1 run of 7',
  '1 run of 8',
  '1 run of 9',
  '2 sets of 4',
  '7 cards of one color',
  '1 set of 5 + 1 set of 2',
  '1 set of 5 + 1 set of 3'
];

export default function Phase10Game({
  activeGame,
  gameState,
  onUpdateGameState,
  onEndGame,
  onQuitGame,
  onEditGame,
  socket,
  isHistoricalView = false
}) {
  const allPlayers = activeGame.players || [];

  // Player order - can be reordered independently of scores
  const [playerOrder, setPlayerOrder] = useState(() => {
    if (gameState.playerOrder) return gameState.playerOrder;
    return allPlayers.map(p => p.user_id);
  });

  // Get ordered players based on playerOrder
  const players = playerOrder.map(uid => allPlayers.find(p => p.user_id === uid)).filter(Boolean);

  // Find dealer by user_id, not index
  const dealerUserId = gameState.dealerUserId || allPlayers.find(p => p.is_dealer)?.user_id || allPlayers[0]?.user_id;
  const dealerIndex = players.findIndex(p => p.user_id === dealerUserId);

  // Initialize scores keyed by user_id: { [user_id]: [round scores array] }
  const [scores, setScores] = useState(() => {
    if (gameState.scores && typeof gameState.scores === 'object' && !Array.isArray(gameState.scores)) {
      // New format: scores keyed by user_id
      return gameState.scores;
    }
    // Migrate from old format or initialize empty
    if (gameState.scores && Array.isArray(gameState.scores)) {
      const migratedScores = {};
      allPlayers.forEach((player, i) => {
        migratedScores[player.user_id] = gameState.scores[i] || Array(10).fill('');
      });
      return migratedScores;
    }
    // Initialize empty scores for all players
    const emptyScores = {};
    allPlayers.forEach(player => {
      emptyScores[player.user_id] = Array(10).fill('');
    });
    return emptyScores;
  });

  const [shareStatus, setShareStatus] = useState('');
  const [exportStatus, setExportStatus] = useState('');
  const [gameLog, setGameLog] = useState(() => {
    // Initialize game log from persisted state
    return gameState.gameLog || [];
  });
  const [showGameLog, setShowGameLog] = useState(true);
  const [draggedColumn, setDraggedColumn] = useState(null);
  const dragOverColumn = useRef(null);
  const [showRoundSummary, setShowRoundSummary] = useState(false);
  const [completedRound, setCompletedRound] = useState(null);
  const prevRoundRef = useRef(null);

  // Debounce refs for score logging
  const scoreDebounceTimers = useRef({});
  const pendingScoreUpdates = useRef({});

  // Touch drag state
  const touchDragState = useRef({
    isDragging: false,
    startY: 0,
    draggedUserId: null,
    longPressTimer: null
  });

  // Helper to merge game logs without losing entries
  // This prevents accidental overwrites when stale data is saved
  const mergeGameLogs = (existingLog, incomingLog) => {
    if (!incomingLog || incomingLog.length === 0) return existingLog;
    if (!existingLog || existingLog.length === 0) return incomingLog;

    // Create a map of existing entries by ID
    const existingIds = new Set(existingLog.map(entry => entry.id));

    // Find entries in incoming that don't exist in current
    const newEntries = incomingLog.filter(entry => !existingIds.has(entry.id));

    // Also check for entries in existing that aren't in incoming (preserve them)
    const incomingIds = new Set(incomingLog.map(entry => entry.id));
    const preservedEntries = existingLog.filter(entry => !incomingIds.has(entry.id));

    // Merge all entries and sort by ID (timestamp) descending
    const merged = [...existingLog, ...newEntries];
    merged.sort((a, b) => b.id - a.id);

    return merged;
  };

  // Sync scores, playerOrder, and gameLog with game state
  useEffect(() => {
    if (gameState.scores && typeof gameState.scores === 'object' && !Array.isArray(gameState.scores)) {
      setScores(gameState.scores);
    }
    if (gameState.playerOrder) {
      setPlayerOrder(gameState.playerOrder);
    }
    if (gameState.gameLog) {
      // Merge incoming log with existing to prevent accidental overwrites
      setGameLog(currentLog => mergeGameLogs(currentLog, gameState.gameLog));
    }
  }, [gameState.scores, gameState.playerOrder, gameState.gameLog]);

  // Listen for real-time updates
  useEffect(() => {
    if (socket) {
      // Listen for single cell updates (using user_id)
      socket.on('score-cell-update', ({ userId, roundIndex, value }) => {
        setScores(prev => {
          const newScores = { ...prev };
          if (!newScores[userId]) newScores[userId] = Array(10).fill('');
          newScores[userId] = [...newScores[userId]];
          newScores[userId][roundIndex] = value;

          // Check if we need to add more rounds
          const numRounds = newScores[userId].length;
          const allPlayersHaveLastRoundScore = players.every(p =>
            newScores[p.user_id]?.[numRounds - 1] !== '' && newScores[p.user_id]?.[numRounds - 1] !== undefined
          );
          if (allPlayersHaveLastRoundScore) {
            Object.keys(newScores).forEach(uid => {
              newScores[uid] = [...newScores[uid], ''];
            });
          }

          return newScores;
        });
      });

      // Listen for player order changes
      socket.on('player-order-update', ({ playerOrder: newOrder }) => {
        setPlayerOrder(newOrder);
      });

      // Listen for full game state updates
      socket.on('game-state', (state) => {
        if (state.scores && typeof state.scores === 'object' && !Array.isArray(state.scores)) {
          setScores(state.scores);
        }
        if (state.playerOrder) {
          setPlayerOrder(state.playerOrder);
        }
      });
    }
  }, [socket, players]);

  const updateScore = (userId, roundIndex, value) => {
    const player = allPlayers.find(p => p.user_id === userId);
    const playerName = player?.name || 'Unknown';
    const oldValue = scores[userId]?.[roundIndex];
    const key = `${userId}-${roundIndex}`;

    // Store initial value for debounced logging (only on first change)
    if (!pendingScoreUpdates.current[key]) {
      pendingScoreUpdates.current[key] = {
        initialValue: oldValue,
        userId,
        roundIndex,
        playerName
      };
    }

    const newScores = { ...scores };
    if (!newScores[userId]) newScores[userId] = Array(10).fill('');
    newScores[userId] = [...newScores[userId]];
    newScores[userId][roundIndex] = value;

    // Check if we need to add more rounds
    const numRounds = newScores[userId].length;
    const allPlayersHaveLastRoundScore = players.every(p =>
      newScores[p.user_id]?.[numRounds - 1] !== '' && newScores[p.user_id]?.[numRounds - 1] !== undefined
    );

    if (allPlayersHaveLastRoundScore) {
      Object.keys(newScores).forEach(uid => {
        newScores[uid] = [...newScores[uid], ''];
      });
    }

    setScores(newScores);

    // Broadcast single cell update to other players immediately
    if (socket) {
      socket.emit('score-cell-update', {
        gameCode: activeGame.id,
        userId,
        roundIndex,
        value
      });
    }

    // Clear existing debounce timer for this cell
    if (scoreDebounceTimers.current[key]) {
      clearTimeout(scoreDebounceTimers.current[key]);
    }

    // Debounce the logging and backend save (800ms delay)
    scoreDebounceTimers.current[key] = setTimeout(() => {
      const pending = pendingScoreUpdates.current[key];
      if (!pending) return;

      const initialValue = pending.initialValue;
      const finalValue = value;
      const timestamp = new Date().toLocaleTimeString();

      let newLogEntry = null;
      if (initialValue === '' || initialValue === undefined) {
        if (finalValue !== '') {
          // Calculate phase progression
          const scoreNum = parseInt(finalValue);
          const completedPhase = !isNaN(scoreNum) && scoreNum < 50;

          // Calculate what phase they were on before this round
          let phaseBefore = 1;
          const playerScores = newScores[userId] || [];
          for (let r = 0; r < roundIndex; r++) {
            const s = parseInt(playerScores[r]);
            if (!isNaN(s) && s < 50) {
              phaseBefore = Math.min(11, phaseBefore + 1);
            }
          }

          let phaseMessage = '';
          if (completedPhase) {
            const nextPhase = Math.min(11, phaseBefore + 1);
            if (nextPhase > 10) {
              phaseMessage = ' - Completed Phase 10! 🎉';
            } else {
              phaseMessage = ` - Completed phase, moving to Phase ${nextPhase}`;
            }
          } else {
            phaseMessage = ` - Remaining on Phase ${phaseBefore}`;
          }

          newLogEntry = {
            id: Date.now(),
            time: timestamp,
            message: `${playerName} scored ${finalValue} points for round ${roundIndex + 1}${phaseMessage}`,
            type: 'score'
          };
        }
      } else if (finalValue !== initialValue) {
        newLogEntry = {
          id: Date.now(),
          time: timestamp,
          message: `${playerName}'s score for round ${roundIndex + 1} was changed from ${initialValue} to ${finalValue || '(cleared)'}`,
          type: 'edit'
        };
      }

      // Clear pending update
      delete pendingScoreUpdates.current[key];
      delete scoreDebounceTimers.current[key];

      // Get latest state for saving - use functional updates to avoid stale closures
      setScores(currentScores => {
        setGameLog(currentGameLog => {
          const updatedGameLog = newLogEntry ? [newLogEntry, ...currentGameLog] : currentGameLog;
          // Save to backend - includes gameLog
          onUpdateGameState({ ...gameState, scores: currentScores, playerOrder, dealerUserId, gameLog: updatedGameLog });
          return updatedGameLog;
        });
        return currentScores;
      });
    }, 800);
  };

  // Handle column drag for reordering players
  const handleColumnDragStart = (e, userId) => {
    setDraggedColumn(userId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleColumnDragOver = (e, userId) => {
    e.preventDefault();
    dragOverColumn.current = userId;
  };

  const handleColumnDragEnd = () => {
    if (draggedColumn && dragOverColumn.current && draggedColumn !== dragOverColumn.current) {
      const newOrder = [...playerOrder];
      const fromIndex = newOrder.indexOf(draggedColumn);
      const toIndex = newOrder.indexOf(dragOverColumn.current);

      // Log the reorder
      const fromPlayer = allPlayers.find(p => p.user_id === draggedColumn);
      const timestamp = new Date().toLocaleTimeString();
      const newLogEntry = {
        id: Date.now(),
        time: timestamp,
        message: `${fromPlayer?.name} moved from position ${fromIndex + 1} to position ${toIndex + 1}`,
        type: 'reorder'
      };

      // Reorder the array
      newOrder.splice(fromIndex, 1);
      newOrder.splice(toIndex, 0, draggedColumn);

      setPlayerOrder(newOrder);

      // Broadcast the order change
      if (socket) {
        socket.emit('player-order-update', {
          gameCode: activeGame.id,
          playerOrder: newOrder
        });
      }

      // Use functional update to get latest gameLog and avoid stale closure
      setGameLog(currentGameLog => {
        const newGameLog = [newLogEntry, ...currentGameLog];
        // Save to backend - includes gameLog
        onUpdateGameState({ ...gameState, scores, playerOrder: newOrder, dealerUserId, gameLog: newGameLog });
        return newGameLog;
      });
    }
    setDraggedColumn(null);
    dragOverColumn.current = null;
  };

  // Touch-based drag handlers to prevent scroll during drag
  const handleTouchStart = (e, userId) => {
    const touch = e.touches[0];
    touchDragState.current.startY = touch.clientY;
    touchDragState.current.draggedUserId = userId;

    // Long press to initiate drag (150ms)
    touchDragState.current.longPressTimer = setTimeout(() => {
      touchDragState.current.isDragging = true;
      setDraggedColumn(userId);
      // Haptic feedback if available
      if (navigator.vibrate) navigator.vibrate(50);
    }, 150);
  };

  const handleTouchMove = (e, userId) => {
    // Cancel long press if moved too much before drag started
    if (!touchDragState.current.isDragging) {
      const touch = e.touches[0];
      const moveDistance = Math.abs(touch.clientY - touchDragState.current.startY);
      if (moveDistance > 10) {
        clearTimeout(touchDragState.current.longPressTimer);
        return;
      }
    }

    // Prevent scrolling while dragging
    if (touchDragState.current.isDragging) {
      e.preventDefault();
      dragOverColumn.current = userId;
    }
  };

  const handleTouchEnd = () => {
    clearTimeout(touchDragState.current.longPressTimer);

    if (touchDragState.current.isDragging) {
      handleColumnDragEnd();
    }

    touchDragState.current.isDragging = false;
    touchDragState.current.draggedUserId = null;
  };

  // Calculate current phase for a player based on scores
  // Score < 50 = completed the phase that round
  const calculatePhase = (userId) => {
    let phase = 1;
    const playerScores = scores[userId] || [];

    for (let r = 0; r < playerScores.length; r++) {
      const score = parseInt(playerScores[r]);
      if (!isNaN(score) && score < 50) {
        phase = Math.min(11, phase + 1); // Cap at 11 (completed all 10)
      }
    }
    return phase;
  };

  const getTotal = (userId) => {
    return (scores[userId] || []).reduce((sum, s) => sum + (parseInt(s) || 0), 0);
  };

  const hasAnyScores = () => {
    return players.some(p => scores[p.user_id]?.some(s => s !== ''));
  };

  // Check if a score is valid (must end in 0 or 5, or be empty)
  const isValidScore = (scoreValue) => {
    if (scoreValue === '' || scoreValue === undefined) return true;
    const score = parseInt(scoreValue);
    if (isNaN(score)) return true;
    return score % 5 === 0;
  };

  // Check if there are any invalid scores in the game
  const hasInvalidScores = () => {
    return players.some(p => {
      const playerScores = scores[p.user_id] || [];
      return playerScores.some(s => !isValidScore(s));
    });
  };

  const getCurrentRound = () => {
    const numRounds = scores[players[0]?.user_id]?.length || 10;
    for (let r = 0; r < numRounds; r++) {
      const allFilled = players.every(p => scores[p.user_id]?.[r] !== '' && scores[p.user_id]?.[r] !== undefined);
      if (!allFilled) return r;
    }
    return numRounds;
  };

  // Detect when a round completes and show the summary popup (with delay for multi-digit entry)
  const roundSummaryTimerRef = useRef(null);
  const lastTriggeredRoundRef = useRef(null);

  useEffect(() => {
    const currentRound = getCurrentRound();
    const gameComplete = players.some(p => calculatePhase(p.user_id) > 10);

    // Only trigger when round advances (not on initial load) and game isn't complete
    // Also check that we haven't already triggered for this round
    if (prevRoundRef.current !== null &&
        currentRound > prevRoundRef.current &&
        currentRound !== lastTriggeredRoundRef.current &&
        !gameComplete &&
        !isHistoricalView) {

      // Mark this round as triggered so we don't re-trigger
      const completedRoundNum = prevRoundRef.current;
      lastTriggeredRoundRef.current = currentRound;

      // Clear any pending timer and start a new one
      if (roundSummaryTimerRef.current) {
        clearTimeout(roundSummaryTimerRef.current);
      }

      roundSummaryTimerRef.current = setTimeout(() => {
        setCompletedRound(completedRoundNum);
        setShowRoundSummary(true);
      }, 1500);
    }

    prevRoundRef.current = currentRound;
  }, [scores]);

  // Cleanup timer on unmount only
  useEffect(() => {
    return () => {
      if (roundSummaryTimerRef.current) {
        clearTimeout(roundSummaryTimerRef.current);
      }
    };
  }, []);

  const getLeaderboard = () => {
    const playerData = players.map((player, i) => ({
      ...player,
      total: getTotal(player.user_id),
      phase: calculatePhase(player.user_id),
      index: i
    }));

    if (!hasAnyScores()) {
      return playerData;
    }

    // Sort by phase (desc) then by score (asc)
    return playerData.sort((a, b) => {
      if (b.phase !== a.phase) return b.phase - a.phase;
      return a.total - b.total;
    });
  };

  const isGameComplete = () => {
    // Game ends when any player completes all 10 phases (phase becomes 11)
    return players.some(p => calculatePhase(p.user_id) > 10);
  };

  const getMedal = (rank) => ['🥇', '🥈', '🥉'][rank] || `#${rank + 1}`;

  const getRank = (userId, leaderboard) => {
    if (!hasAnyScores()) return null;
    const playerData = leaderboard.find(p => p.user_id === userId);
    if (!playerData) return 0;

    // Find rank accounting for ties (same phase and same total = same rank)
    let rank = 0;
    let prevPhase = null;
    let prevTotal = null;
    for (let i = 0; i < leaderboard.length; i++) {
      if (leaderboard[i].phase !== prevPhase || leaderboard[i].total !== prevTotal) {
        rank = i;
        prevPhase = leaderboard[i].phase;
        prevTotal = leaderboard[i].total;
      }
      if (leaderboard[i].user_id === userId) {
        return rank;
      }
    }
    return 0;
  };

  const handleFinishGame = () => {
    const leaderboard = getLeaderboard();
    const finalScores = leaderboard.map((player) => ({
      user_id: player.user_id,
      score: player.total,
      round_scores: (scores[player.user_id] || []).map(s => parseInt(s) || 0)
    }));

    onEndGame(finalScores);
  };

  const shareScorecard = async () => {
    const leaderboard = getLeaderboard();
    const gameComplete = isGameComplete();
    const currentRound = getCurrentRound();

    let text = `🔟 PHASE 10 🔟\n`;
    text += `Round ${currentRound + 1}\n\n`;

    if (gameComplete) {
      const winner = leaderboard[0];
      text += `🏆 ${winner.name} WINS! 🏆\n\n`;
    }

    text += `📊 STANDINGS:\n`;
    leaderboard.forEach((player, i) => {
      const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`;
      text += `${medal} ${player.name}: Phase ${player.phase}, ${player.total} pts\n`;
    });

    try {
      if (navigator.share) {
        await navigator.share({ text });
        setShareStatus('Shared!');
      } else {
        await navigator.clipboard.writeText(text);
        setShareStatus('Copied!');
      }
    } catch (err) {
      try {
        await navigator.clipboard.writeText(text);
        setShareStatus('Copied!');
      } catch (e) {
        setShareStatus('Failed');
      }
    }
    setTimeout(() => setShareStatus(''), 2000);
  };

  const exportGame = async () => {
    const gameData = {
      game: 'Phase 10',
      date: new Date().toISOString(),
      join_code: activeGame.join_code,
      players: players.map(player => ({
        name: player.name,
        phase: calculatePhase(player.user_id),
        roundScores: scores[player.user_id] || [],
        total: getTotal(player.user_id)
      })),
      phases: PHASES,
      currentRound: getCurrentRound(),
      isComplete: isGameComplete()
    };

    try {
      await navigator.clipboard.writeText(JSON.stringify(gameData, null, 2));
      setExportStatus('Copied!');
    } catch (err) {
      setExportStatus('Failed');
    }
    setTimeout(() => setExportStatus(''), 2000);
  };

  const copyAllGameData = async () => {
    const leaderboard = getLeaderboard();
    const gameData = {
      game: 'Phase 10',
      exportedAt: new Date().toISOString(),
      gameId: activeGame.id,
      joinCode: activeGame.join_code,
      status: isGameComplete() ? 'complete' : 'in_progress',
      currentRound: getCurrentRound() + 1,
      players: players.map((player, i) => ({
        name: player.name,
        id: player.user_id,
        displayOrder: i + 1,
        isDealer: player.user_id === dealerUserId,
        currentPhase: calculatePhase(player.user_id),
        phaseDescription: PHASES[calculatePhase(player.user_id) - 1] || 'Complete',
        roundScores: (scores[player.user_id] || []).map((score, ri) => ({
          round: ri + 1,
          score: score !== '' ? parseInt(score) || 0 : null
        })),
        totalScore: getTotal(player.user_id),
        rank: getRank(player.user_id, leaderboard) + 1
      })),
      phases: PHASES,
      activityLog: gameLog.map(log => ({
        time: log.time,
        action: log.message,
        type: log.type
      })),
      leaderboard: leaderboard.map((player, rank) => ({
        rank: rank + 1,
        name: player.name,
        totalScore: player.total,
        currentPhase: player.phase
      }))
    };

    try {
      await navigator.clipboard.writeText(JSON.stringify(gameData, null, 2));
      return true;
    } catch (err) {
      return false;
    }
  };

  const leaderboard = getLeaderboard();
  const gameComplete = isGameComplete();
  const currentRound = getCurrentRound();
  const currentDealer = (dealerIndex + currentRound) % players.length;
  const gameHasScores = hasAnyScores();
  const numRounds = scores[players[0]?.user_id]?.length || 10;

  // Find winner (first player to complete phase 10)
  let winnerUserId = null;
  players.forEach(p => {
    if (calculatePhase(p.user_id) > 10) {
      winnerUserId = p.user_id;
    }
  });

  return (
    <div className="p-2 bg-gradient-to-br from-cyan-900 to-cyan-950 min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center mb-2">
        <button
          onClick={onQuitGame}
          className="text-cyan-300 hover:text-white text-sm"
        >
          ← {isHistoricalView ? 'Back' : 'Exit'}
        </button>
        <div className="text-center">
          <h1 className="text-xl font-bold text-cyan-400">🔟 Phase 10 🔟</h1>
          {isHistoricalView ? (
            <p className="text-cyan-300 text-xs">📜 Viewing Completed Game</p>
          ) : (
            <>
              <p className="text-cyan-300 text-xs">
                {gameComplete ? 'Game Complete!' : `Round ${currentRound + 1}`}
              </p>
              {!gameComplete && (
                <p className="text-cyan-200 text-xs">
                  🃏 {players[currentDealer]?.name} dealing
                </p>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isHistoricalView && (
            <button
              onClick={onEditGame}
              className="text-cyan-300 hover:text-white text-sm"
              title="Edit game settings"
            >
              ⚙️
            </button>
          )}
          <div className="text-cyan-300 text-xs font-mono">
            {activeGame.join_code || ''}
          </div>
        </div>
      </div>

      {/* Winner Banner */}
      {gameComplete && winnerUserId && (
        <div className="bg-gradient-to-r from-cyan-500 via-teal-400 to-cyan-500 rounded-xl p-4 mb-4 text-center">
          <div className="text-3xl mb-1">🎉🏆🎉</div>
          <div className="text-cyan-950 font-bold text-xl">{allPlayers.find(p => p.user_id === winnerUserId)?.name} WINS!</div>
          <div className="text-cyan-900 text-sm">
            Completed all 10 phases with {getTotal(winnerUserId)} points
          </div>
        </div>
      )}

      {/* Leaderboard */}
      <div className="bg-cyan-950/80 rounded-lg p-3 mb-4">
        <h2 className="text-cyan-400 font-bold text-sm mb-2">📊 STANDINGS</h2>
        <div className="space-y-1">
          {leaderboard.map((player, i) => (
            <div
              key={player.user_id}
              className={`flex items-center justify-between px-3 py-2 rounded ${
                winnerUserId === player.user_id ? 'bg-yellow-500/30 border border-yellow-400' :
                i === 0 && gameHasScores ? 'bg-cyan-500/30' : 'bg-cyan-800/30'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-lg">
                  {winnerUserId === player.user_id ? '🏆' : gameHasScores ? getMedal(i) : `${i + 1}.`}
                </span>
                <div
                  className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white"
                  style={{ backgroundColor: player.avatar_color || '#6366f1' }}
                >
                  {player.name?.charAt(0)}
                </div>
                <div>
                  <span className="text-white font-medium">{player.name}</span>
                  <span className="text-cyan-400 text-xs ml-2">Phase {player.phase > 10 ? '10 ✓' : player.phase}</span>
                </div>
              </div>
              <span className="text-white font-bold font-mono">{gameHasScores ? player.total : '–'}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Phase Reference */}
      <div className="bg-cyan-950/50 rounded-lg p-3 mb-4">
        <h3 className="text-cyan-400 font-bold text-xs mb-2">PHASES (score &lt; 50 = phase complete)</h3>
        <div className="grid grid-cols-2 gap-1 text-xs">
          {PHASES.map((phase, i) => (
            <div key={i} className="text-cyan-200">
              <span className="text-cyan-400 font-mono">{i + 1}.</span> {phase}
            </div>
          ))}
        </div>
        <div className="mt-2 pt-2 border-t border-cyan-700/30 text-xs text-cyan-300">
          <span className="text-cyan-400 font-bold">Points:</span> 1-9 = 5 • 10-12 = 10 • Skip = 15 • Wild = 25
        </div>
      </div>

      {/* Score Table - Editable with draggable columns */}
      <div className="overflow-x-auto rounded-lg mb-4 flex justify-center">
        <table className="text-sm border-collapse">
          <thead>
            <tr className="bg-cyan-950">
              <th className="p-1.5 text-cyan-400 text-xs sticky left-0 bg-cyan-950 z-10 w-12">Rnd</th>
              {players.map((player, i) => {
                const phase = calculatePhase(player.user_id);
                const isDealer = currentDealer === i && !gameComplete;
                const isLeader = leaderboard[0]?.user_id === player.user_id && gameHasScores;
                const isWinner = winnerUserId === player.user_id;
                const isDragging = draggedColumn === player.user_id;
                return (
                  <th
                    key={player.user_id}
                    draggable
                    onDragStart={(e) => handleColumnDragStart(e, player.user_id)}
                    onDragOver={(e) => handleColumnDragOver(e, player.user_id)}
                    onDragEnd={handleColumnDragEnd}
                    onTouchStart={(e) => handleTouchStart(e, player.user_id)}
                    onTouchMove={(e) => handleTouchMove(e, player.user_id)}
                    onTouchEnd={handleTouchEnd}
                    className={`p-1 min-w-[80px] max-w-[120px] transition-all cursor-grab active:cursor-grabbing select-none ${
                      isDealer ? 'bg-amber-500/20' : ''
                    } ${isWinner ? 'bg-yellow-500/30' : ''} ${isDragging ? 'opacity-50' : ''}`}
                    title="Drag to reorder"
                  >
                    <div className={`text-xs font-medium p-1 rounded ${
                      isWinner ? 'text-yellow-300 font-bold' :
                      isDealer ? 'text-amber-300 font-bold' :
                      isLeader ? 'text-cyan-300' : 'text-white'
                    }`}>
                      {isWinner ? '🏆 ' : isDealer ? '🃏 ' : ''}{player.name?.split(' ')[0]}
                      <div className="text-cyan-400 text-xs font-normal">P{phase > 10 ? '10✓' : phase}</div>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: numRounds }).map((_, ri) => (
              <tr key={ri} className={`border-t border-cyan-700/30 ${ri === currentRound && !gameComplete ? 'bg-cyan-700/20' : ''}`}>
                <td className={`p-1.5 font-bold text-xs text-center sticky left-0 z-10 ${ri === currentRound && !gameComplete ? 'bg-cyan-800 text-cyan-200' : 'bg-cyan-900 text-cyan-400'}`}>
                  {ri === currentRound && !gameComplete ? '▶ ' : ''}{ri + 1}
                </td>
                {players.map((player, pi) => {
                  const scoreValue = scores[player.user_id]?.[ri] || '';
                  const score = parseInt(scoreValue);
                  const completedPhase = !isNaN(score) && score < 50;
                  const isDealer = currentDealer === pi && !gameComplete;
                  const isWinner = winnerUserId === player.user_id;
                  const isInvalid = !isValidScore(scoreValue);
                  return (
                    <td key={player.user_id} className={`p-0.5 ${isDealer ? 'bg-amber-500/10' : ''} ${isWinner ? 'bg-yellow-500/10' : ''}`}>
                      <input
                        type="number"
                        inputMode="numeric"
                        value={scoreValue}
                        onChange={(e) => updateScore(player.user_id, ri, e.target.value)}
                        className={`w-full text-center rounded p-1.5 text-sm focus:outline-none focus:ring-1 ${
                          isInvalid
                            ? 'bg-red-500/40 text-red-200 font-bold ring-1 ring-red-400 focus:ring-red-400'
                            : completedPhase
                            ? 'bg-green-500/30 text-green-300 font-bold focus:ring-cyan-400'
                            : 'bg-cyan-800/50 text-white focus:ring-cyan-400'
                        }`}
                        placeholder="–"
                        title={isInvalid ? 'Invalid score - must end in 0 or 5' : ''}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
            <tr className="bg-cyan-950 font-bold border-t-2 border-cyan-600">
              <td className="p-2 text-cyan-400 text-xs sticky left-0 bg-cyan-950 z-10 w-12">TOTAL</td>
              {players.map((player, pi) => {
                const isLeader = leaderboard[0]?.user_id === player.user_id && gameHasScores;
                const isDealer = currentDealer === pi && !gameComplete;
                const isWinner = winnerUserId === player.user_id;
                return (
                  <td key={player.user_id} className={`p-2 text-center text-base ${isDealer ? 'bg-amber-500/10' : ''} ${isWinner ? 'bg-yellow-500/20' : ''} ${isLeader ? 'text-cyan-300' : 'text-white'}`}>
                    {getTotal(player.user_id)}
                  </td>
                );
              })}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Invalid Scores Warning */}
      {hasInvalidScores() && !isHistoricalView && (
        <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 mb-4 text-center">
          <p className="text-red-300 text-sm font-medium">
            ⚠️ Invalid scores detected - scores must end in 0 or 5
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-center gap-2 flex-wrap">
        <button
          onClick={shareScorecard}
          className="bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg text-sm font-medium"
        >
          📤 {shareStatus || 'Share'}
        </button>
        <button
          onClick={exportGame}
          className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-lg text-sm font-medium"
        >
          📋 {exportStatus || 'Export'}
        </button>
        {!isHistoricalView && (
          <button
            onClick={() => window.open(`/game/${activeGame.id}/tv`, '_blank')}
            className="bg-purple-600 hover:bg-purple-500 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            📺 TV Mode
          </button>
        )}
        {(gameComplete || isHistoricalView) && (
          <button
            onClick={handleFinishGame}
            disabled={hasInvalidScores() && !isHistoricalView}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${
              hasInvalidScores() && !isHistoricalView
                ? 'bg-gray-500 text-gray-300 cursor-not-allowed'
                : 'bg-cyan-500 hover:bg-cyan-400 text-black'
            }`}
            title={hasInvalidScores() && !isHistoricalView ? 'Fix invalid scores before finishing' : ''}
          >
            🏆 {isHistoricalView ? 'View Podium' : 'Finish Game'}
          </button>
        )}
      </div>

      {/* Game Log */}
      <div className="mt-4 bg-blue-950/80 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between p-3 border-b border-blue-700/30">
          <button
            onClick={() => setShowGameLog(!showGameLog)}
            className="flex items-center gap-2 text-cyan-400 font-bold text-sm"
          >
            📋 Game Log
            <span className="text-blue-400 text-xs">({gameLog.length})</span>
            <span className="text-blue-500 text-xs">{showGameLog ? '▼' : '▶'}</span>
          </button>
          <button
            onClick={async () => {
              const success = await copyAllGameData();
              if (success) {
                setExportStatus('Copied!');
                setTimeout(() => setExportStatus(''), 2000);
              }
            }}
            className="bg-slate-600 hover:bg-slate-500 text-white px-3 py-1 rounded text-xs font-medium"
          >
            📋 Copy All Game Data
          </button>
        </div>
        {showGameLog && (
          <div className="max-h-40 overflow-y-auto">
            {gameLog.length === 0 ? (
              <div className="p-3 text-blue-500 text-sm text-center">
                No activity yet. Score changes will appear here.
              </div>
            ) : (
              <div className="divide-y divide-blue-800/50">
                {gameLog.map(log => (
                  <div key={log.id} className="px-3 py-2 flex items-start gap-2">
                    <span className="text-blue-600 text-xs font-mono whitespace-nowrap">{log.time}</span>
                    <span className={`text-sm ${
                      log.type === 'edit' ? 'text-amber-300' :
                      log.type === 'reorder' ? 'text-purple-300' :
                      log.type === 'settings' ? 'text-cyan-300' :
                      'text-blue-200'
                    }`}>
                      {log.message}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Round Summary Popup */}
      {showRoundSummary && completedRound !== null && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
          <div className="bg-gradient-to-br from-cyan-900 to-cyan-950 rounded-xl p-6 max-w-sm w-full border border-cyan-600/50 shadow-xl">
            <div className="text-center mb-4">
              <h3 className="text-xl font-bold text-cyan-400">
                Round {completedRound + 1} Complete!
              </h3>
              <p className="text-cyan-300 text-sm mt-1">
                Next up: Round {completedRound + 2}
              </p>
            </div>

            {/* Leaderboard */}
            <div className="bg-cyan-950/80 rounded-lg p-3 mb-4">
              <h4 className="text-cyan-400 font-bold text-sm mb-2">📊 Standings</h4>
              <div className="space-y-1">
                {getLeaderboard().map((player, i) => (
                  <div
                    key={player.user_id}
                    className={`flex items-center justify-between px-2 py-1.5 rounded ${
                      i === 0 && hasAnyScores() ? 'bg-cyan-500/30' : 'bg-cyan-800/30'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{getMedal(i)}</span>
                      <div
                        className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white"
                        style={{ backgroundColor: player.avatar_color || '#6366f1' }}
                      >
                        {player.name?.charAt(0)}
                      </div>
                      <div>
                        <span className={`text-sm font-medium ${i === 0 ? 'text-cyan-300' : 'text-white'}`}>
                          {player.name}
                        </span>
                        <span className="text-cyan-400 text-xs ml-2">P{player.phase > 10 ? '10✓' : player.phase}</span>
                      </div>
                    </div>
                    <span className={`font-bold font-mono ${i === 0 ? 'text-cyan-300' : 'text-white'}`}>
                      {player.total}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Next Dealer */}
            <div className="bg-amber-500/20 border border-amber-500/30 rounded-lg p-3 mb-4 text-center">
              <p className="text-amber-300 text-sm">🃏 Next Dealer</p>
              <p className="text-white font-bold text-lg">
                {players[(dealerIndex + completedRound + 1) % players.length]?.name}
              </p>
            </div>

            <button
              onClick={() => setShowRoundSummary(false)}
              className="w-full bg-cyan-600 hover:bg-cyan-500 text-white py-3 rounded-lg font-bold text-lg"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
